import React from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'src/ui/library/styled';
import debounce from 'lodash.debounce';

const styles = (theme, props) => ({
  root: {
    border: `solid ${props.padding}px transparent`,
    position: 'relative',
    overflow: 'hidden',
  },
  active: {
    boxShadow: '0 0 7px 0 rgba(0,0,0,0.11)',
    border: `solid ${props.padding}px #FFF`,
    backgroundColor: '#FFF',
    height: '100%',
    overflow: 'visible',
    '& $coverBoxshadow': {
      visibility: 'visible',
    },
  },
  trigger: {
    position: 'relative',
    cursor: 'pointer',
  },
  content: {
    position: 'absolute',
    overflow: 'visible',
    width: '100%',
    left: `-${props.padding}px`,
    padding: `16px ${props.padding}px 16px ${props.padding}px`,
    backgroundColor: '#FFF',
    boxShadow: '0 0 8px 0 rgba(0, 0, 0, 0.11)',
  },
  coverBoxshadow: {
    visibility: 'hidden',
    position: 'absolute',
    bottom: '-5px',
    width: `calc(100% + ${props.padding * 2}px)`,
    height: '6px',
    backgroundColor: '#FFF',
    zIndex: '1300',
    left: `-${props.padding}px`,
  },
});

class PopoutBase extends React.Component {
  static propTypes = {
    /** Orientation on where the Popout should open (left, right) */
    orientation: PropTypes.string,
    /** Container element - useful for pulling width/height from */
    container: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    /** Allow hover to trigger open */
    hoverable: PropTypes.bool,
    /** Trigger for the Popout */
    trigger: PropTypes.node.isRequired,
    /** Content to display when Popout triggered */
    content: PropTypes.node.isRequired,
    /** Default padding to be applied on Popout */
    padding: PropTypes.number,
    /** Use width from parent container for Popout content width */
    useContainerWidth: PropTypes.bool,
    /** Reference callback to toggleActive() to trigger manual close of Popout */
    triggerClose: PropTypes.func,
    /** Reference callback to toggleActive() to trigger manual close of Popout */
    triggerOpen: PropTypes.func,
    /** Callback fired when Popout is opened */
    onOpen: PropTypes.func,
    /** Callback fired when Popout is closed */
    onClosed: PropTypes.func,
  };

  static defaultProps = {
    styleType: 'default',
    orientation: 'left',
    styles: {},
    autoResize: true,
    hoverable: true,
    padding: 8,
    open: false,
  };

  state = {
    activeFlag: this.props.open,
    triggerWidth: 'auto',
    contentWidth: 'auto',
    contentHeight: 'auto',
    triggerHeight: 'auto',
  };

  componentDidMount() {
    this.ignoreModals = true;

    this.setDimensions();
    setTimeout(() => this.setDimensions(), 100); /* work around for now to retrieve true width of child */

    // there's a bug today where the width's sometimes are not correct when pulling the contentWidth for the provided Popout content
    // best work around for now is to disable autoResize if needed
    if (this.props.autoResize) {
      this._handleResize = debounce(this.handleResize, 250);
      window.addEventListener('resize', this._handleResize, false);
    }

    this.observer = new MutationObserver(this.mutationObserverCallback);
    this.observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.triggerCloseFilters !== this.props.triggerCloseFilters && this.props.triggerCloseFilters === true) {
      this.toggleCasesActive(null);
      if (this.props.setFiltersDivVisibilityState) this.props.setFiltersDivVisibilityState(false);
    }
  }

  mutationObserverCallback = mutations => {
    if (mutations[0].target.style.overflow === 'hidden') this.ignoreModals = false;
    else this.ignoreModals = true;
  };

  componentWillUnmount() {
    if (this.props.autoResize) {
      window.removeEventListener('resize', this._handleResize, false);
    }
    this.observer.disconnect();
  }

  handleResize = () => {
    if (window.innerWidth !== this.windowWidth) {
      this.windowWidth = window.innerWidth;
      this.setDimensions();
    }
  };

  setDimensions = () => {
    const { container } = this.props;

    const triggerEl = findDOMNode(this.triggerRef);
    const contentEl = findDOMNode(this.contentRef);
    let containerEl = null;

    if (triggerEl === null || contentEl === null) return;

    if (container) {
      const { current } = container;
      const targetEl = current === undefined ? container : current;
      containerEl = findDOMNode(targetEl);
    }

    const paddingSize = this.props.padding * 2;
    const newDimensions = {
      triggerHeight: triggerEl.offsetHeight + paddingSize,
      triggerWidth: triggerEl.offsetWidth + paddingSize,
      contentWidth: contentEl.offsetWidth,
      contentTop: triggerEl.offsetHeight + 4,
      parentWidth: (containerEl && containerEl.offsetWidth) || null,
      parentHeight: (containerEl && containerEl.offsetHeight) || null,
      zIndex: 100,
    };
    this.setState(newDimensions);
  };

  getTriggerStyles = () => {};

  getContentStyles = () => {
    const { contentWidth, contentTop, parentWidth, triggerWidth, zIndex } = this.state;

    if (contentWidth === 'auto') return {};

    let finalWidth = triggerWidth > contentWidth ? triggerWidth : this.state.contentWidth;
    let minWidth = contentWidth > triggerWidth ? contentWidth : finalWidth * 2;

    if (finalWidth > window.outerHeight) finalWidth = window.outerHeight;

    if (this.props.useContainerWidth) {
      finalWidth = parentWidth;
      minWidth = 'auto';
    }

    let finalLeft = `-${this.props.padding}px`;

    if (this.props.orientation === 'left') {
      const triggerEl = findDOMNode(this.triggerRef);
      const ElLeftposition = triggerEl && triggerEl.getBoundingClientRect().left;
      if (this.props.dynamicOrient && ElLeftposition < 230) {
        // return nothing to change as to perform right side orientation
      } else {
        if (triggerWidth > finalWidth) {
          finalLeft = -this.state.triggerWidth - this.props.padding;
        } else {
          finalLeft = this.state.triggerWidth - finalWidth - this.props.padding;
          if (!this.props.useContainerWidth) {
            finalLeft -= this.props.padding * 2;
          }
        }

        if (!this.props.useContainerWidth) {
          finalWidth += this.props.padding * 2;
        }
      }
    }

    return {
      top: contentTop,
      width: finalWidth,
      minWidth: minWidth,
      left: finalLeft,
      zIndex: zIndex,
    };
  };

  handleCallbacks = () => {
    const { activeFlag } = this.state;
    if (this.props.onOpen && activeFlag === true) this.props.onOpen();
    if (this.props.onClose && activeFlag === false) this.props.onClose();
  };

  toggleActive = status => {
    this.setState(
      prevState => ({ activeFlag: status !== undefined ? status : !prevState.activeFlag }),
      () => this.handleCallbacks(),
    );
  };

  toggleCasesActive = status => {
    let { activeFlag } = this.state;
    // if (!activeFlag) {
    //   // attach/remove event handler
    //   document.addEventListener('click', this.handleOutsideClick, false);
    // } else {
    //   document.removeEventListener('click', this.handleOutsideClick, false);
    // }

    this.setState(
      prevState => ({ activeFlag: !prevState.activeFlag }),
      () => this.handleCallbacks(),
    );
  };

  handleOutsideClick = e => {
    const contentEl = findDOMNode(this.contentRef);
    // ignore clicks on the component itself
    if ((contentEl && contentEl.contains(e.target)) || e.target.nodeName === 'LI') {
      return;
    }

    // Below code is to handle the calendar events
    let el = e.target;
    while (el) {
      if (typeof el.className === 'string' && (el.className.indexOf('DateFormatInput') > -1 || el.className.indexOf('Calendar') > -1)) {
        return; //// returns if Calendar events found
      }
      el = el.parentNode;
    }

    this.toggleCasesActive();
  };

  handleMouseEnter = status => {
    if (this.props.hoverable) {
      this.setState({ activeFlag: true }, () => this.handleCallbacks());
      if (this.props.casePage && this.props.casePage === true) {
        this.setState({ zIndex: 100 });
      }
    }
  };

  handleMouseLeave = () => {
    if (this.props.hoverable && this.ignoreModals) {
      this.setState({ activeFlag: false }, () => this.handleCallbacks());
      if (this.props.casePage && this.props.casePage === true) {
        this.setState({ zIndex: -1 });
      }
    }
  };

  handleClick = () => {
    this.setState({ activeFlag: !this.state.activeFlag });
  };

  handleBlur = () => {
    this.setState({ activeFlag: false });
  };

  render() {
    const { classes, trigger, content, type } = this.props;
    const { activeFlag, triggerWidth } = this.state;

    const popoutClass = classNames({
      [classes.root]: true,
      [classes.active]: activeFlag,
      'popout-active': activeFlag,
    });

    if (this.props.triggerClose) {
      this.props.triggerClose(this.toggleActive.bind(null, false));
    }

    if (this.props.triggerOpen) {
      this.props.triggerOpen(this.toggleActive.bind(null, true));
    }

    return type && type === 'Cases' ? (
      <div className={popoutClass} style={{ width: triggerWidth }} tabIndex={0}>
        <div className={classes.trigger} styles={this.getTriggerStyles()}>
          {React.cloneElement(trigger, {
            ref: el => (this.triggerRef = el),
            onClick: this.toggleCasesActive,
          })}
          <div className={classes.coverBoxshadow} />
        </div>
        <div className={classes.content} style={this.getContentStyles()}>
          {React.cloneElement(content, {
            ref: el => (this.contentRef = el),
          })}
        </div>
      </div>
    ) : (
      <div
        className={popoutClass}
        style={{ width: triggerWidth }}
        onMouseLeave={this.handleMouseLeave}
        onMouseEnter={this.handleMouseEnter}
        tabIndex={0}>
        <div className={classes.trigger} styles={this.getTriggerStyles()}>
          {React.cloneElement(trigger, {
            ref: el => (this.triggerRef = el),
            onClick: this.toggleActive,
          })}
          <div className={classes.coverBoxshadow} />
        </div>
        <div className={classes.content} style={this.getContentStyles()}>
          {React.cloneElement(content, {
            ref: el => (this.contentRef = el),
          })}
        </div>
      </div>
    );
  }
}

export { PopoutBase };
export default styled(PopoutBase)(styles);



// WEBPACK FOOTER //
// ./src/ui/library/Popout/PopoutBase.js